<template>
    <div class="w-full">
        <div v-if="loading" class="flex justify-center items-center h-full">
            <hs-table-skeleton :columns="7" :rows="per_page + 1" />
        </div>
        <hs-table v-else :headers="headers" :data="requests" :loading="loading" :header-key-mapping="headerKeyMapping">
            <template #cell-name="{ row }">
                {{ row.name || '-' }}
            </template>
            <template #cell-documents="{ row }">
                <a v-if="row.documents && row.documents.length > 0" :href="buildDocumentUrl(row.documents[0].url_path)" target="_blank"
                    class="inline-flex items-center gap-2 text-blue-500 hover:text-blue-700">
                    <vue-feather :type="getFileIcon(row.documents[0].url_path)" size="16" />
                    <span>View Document</span>
                </a>
                <span v-else class="text-gray-500">No document</span>
            </template>
            <template #cell-status="{ row }">
                <hs-badge :tone="statusTone(row.status)" size="sm">
                    {{ row.status }}
                </hs-badge>
            </template>
            <template #cell-reviewed_by="{ row }">
                {{ row.reviewed_by?.names || '-' }}
            </template>
            <template #cell-reviewed_at="{ row }">
                {{ $convertGMTToLocalTime(row.review_date) || '-' }}
            </template>
            <template #cell-actions="{ row }">
                <hs-button variant="secondary" size="sm" icon="eye" @click="viewDetails(row)">
                    View Details
                </hs-button>
            </template>
        </hs-table>
        <hs-pagination :current-page="page" :total-pages="totalPages" :total-items="totalItems"
            :items-per-page="per_page" @previous-page="previousPage" @next-page="nextPage"
            @update:items-per-page="updateItemsPerPage" />

        <view-request-details :request="selectedRequest" :is-store-details="true" v-if="showRequestDetails"
            @close-modal="showRequestDetails = false" @approve-request="approveRequest" @reject-request="rejectRequest"
            :loading-approve="loadingApprove" :loading-reject="loadingReject" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ViewRequestDetails from '@/components/modals/settings/ViewRequestDetails.vue';
import { toast } from 'vue3-toastify';
import { getFileIcon } from '@/helper/utils';
import { HsBadge, HsButton } from '@/components/ui';

export default {
    name: 'ViewStoreDetailsRequests',
    components: {
        ViewRequestDetails,
        HsBadge,
        HsButton
    },
    props: {
        refreshRequests: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            requests: [],
            loading: true,
            error: null,
            page: 1,
            per_page: 8,
            query: '',
            selectedRequest: null,
            showRequestDetails: false,
            loadingApprove: false,
            loadingReject: false,
            headers: [
                'NAME',
                // 'VAT Activated',
                'Documents',
                'Status',
                'Reviewed By',
                'Reviewed At',
                // 'Submitted By',
                // 'Submitted At',
                'Actions'
            ],
            headerKeyMapping: {
                'NAME': 'name',
                // 'VAT Activated': 'vat_activated',
                'Documents': 'documents',
                'Status': 'status',
                'Reviewed By': 'reviewed_by',
                'Reviewed At': 'review_date',
                // 'Submitted By': 'submitted_by',
                // 'Submitted At': 'created_at',
                'Actions': 'actions'
            },
            totalPages: 0,
            totalItems: 0,
            backendUrl: (process.env.VUE_APP_BACKEND_API || '').replace(/\/$/, '')
        }
    },
    computed: {
        ...mapGetters(['loadingStoreDetailsChangeRequests', 'errorStoreDetailsChangeRequests', 'getSelectedStore']),
        currentStoreId() {
            return this.$route.params?.storeId || this.getSelectedStore || this.$store.state.dashboard?.selectedStore;
        }
    },
    methods: {
        buildDocumentUrl(urlPath) {
            if (!urlPath) return null;
            const base = (process.env.VUE_APP_BACKEND_API || '').replace(/\/$/, '');
            // url_path from backend already includes /api/v1/... or /api/v2/...
            // Remove leading /api if base already ends with /api to avoid duplication
            let normalized = urlPath.startsWith('/') ? urlPath : `/${urlPath}`;
            if (base.endsWith('/api') && normalized.startsWith('/api/')) {
                normalized = normalized.substring(4); // Remove '/api' prefix
            }
            return `${base}${normalized}`;
        },
        getFileIcon,
        viewDetails(item) {
            this.selectedRequest = item;
            this.showRequestDetails = true;
        },
        statusTone(status) {
            const map = {
                APPROVED: 'success',
                CANCELED: 'danger',
                REJECTED: 'danger',
                PENDING: 'warning'
            };
            return map[status] || 'neutral';
        },
        previousPage() {
            if (this.page > 1) {
                this.page--;
                this.getRequests();
            }
        },
        nextPage() {
            this.page++;
            this.getRequests();
        },
        updateItemsPerPage(newValue) {
            this.per_page = newValue;
            this.getRequests();
        },
        resetPagination() {
            this.page = 1;
        },
        getRequests() {
            if (!this.currentStoreId) {
                this.requests = [];
                this.totalPages = 0;
                this.totalItems = 0;
                this.loading = false;
                return;
            }
            this.loading = true;
            this.$store.dispatch('getStoreDetailsChangeRequests', {
                page: this.page,
                per_page: this.per_page,
                query: this.query,
                storeId: this.currentStoreId
            }).then((response) => {
                this.requests = response.items || [];
                this.totalPages = response.pages;
                this.totalItems = response.total;
                this.loading = false;
            }).catch((error) => {
                this.error = error;
                this.loading = false;
            });
        },
        approveRequest(requestId) {
            this.loadingApprove = true;
            this.$store.dispatch('reviewStoreDetailsChangeRequests', { requestId, data: { status: 'APPROVED' } }).then((response) => {
                toast.success(response?.message || 'Request approved successfully');
                this.showRequestDetails = false;
                this.loadingApprove = false;
                this.getRequests();
            }).catch((error) => {
                this.loadingApprove = false;
                toast.error(error.response?.data?.message || 'Error while approving request, please try again');
            });
        },
        rejectRequest(requestId) {
            this.loadingReject = true;  
            this.$store.dispatch('reviewStoreDetailsChangeRequests', { requestId, data: { status: 'REJECTED' } }).then((response) => {
                toast.success(response?.message || 'Request rejected successfully');
                this.showRequestDetails = false;
                this.loadingReject = false;
                this.getRequests();
            }).catch((error) => {
                this.loadingReject = false;
                toast.error(error.response?.data?.message || 'Error while rejecting request, please try again');
            });
        }
    },
    mounted() {
        this.getRequests();
    },
    watch: {
        refreshRequests() {
            this.getRequests();
        },
        currentStoreId(newId, oldId) {
            if (!newId || String(newId) === String(oldId)) {
                return;
            }
            this.resetPagination();
            this.getRequests();
        }
    }
}
</script>

<style scoped></style>
